import {z} from "zod/v4-mini"
import {IsNonNull, mapFields} from "@cm/utils"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {EvaluableTemplateNode} from "#template/evaluable-template-node"
import {createGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryAttributeSetData, IndexingDim} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeSetNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"

export type AttributeSetRecordInputParameters<TAttribute, TAttributeSet> = {
    record: Record<string, TAttribute | TAttributeSet | null>
}

export const attributeSetRecordInputParametersSchema = <TAttribute, TAttributeSet>(
    attributeSchema: z.ZodMiniType<TAttribute, TAttribute>,
    attributeSetSchema: z.ZodMiniType<TAttributeSet, TAttributeSet>,
) =>
    z.object({
        record: z.record(z.string(), z.nullable(z.union([attributeSchema, attributeSetSchema]))),
    })

function filterNullEntriesFromRecord<T>(input: Record<string, T | null>): Record<string, T> {
    const filteredEntries: Record<string, T> = {}
    for (const key of Object.keys(input)) {
        if (input[key] === null) continue
        filteredEntries[key] = input[key]!
    }
    return filteredEntries
}

function setupAttributeSetDataFromRecordEntries<T extends IndexingDim>(
    record: Record<string, GeometryAttributeData<T> | GeometryAttributeSetData<T> | null>,
    dim: T,
): GeometryAttributeSetData<T> | null {
    const attributeSetData: Record<string, GeometryAttributeData<T> | GeometryAttributeSetData<T>> = {}
    for (const [key, value] of Object.entries(record)) {
        if (value === null) continue
        if (dim !== value.dim) throw new Error(`Invalid indexing dimension for attribute "${key}": expected ${dim}, got ${value.dim}`)

        attributeSetData[key] = value
    }
    if (Object.keys(attributeSetData).length === 0) return null
    return createGeometryAttributeSetData(attributeSetData, dim)
}

// One shared parameterized base — variants pin `T` and declare their dim-specific marker manually.
export abstract class GeometryAttributeSetRecordInputNode<
    out T extends IndexingDim,
    out TAttribute extends EvaluableTemplateNode<GeometryAttributeData<T> | null>,
    out TAttributeSet extends EvaluableTemplateNode<GeometryAttributeSetData<T> | null>,
> extends GeometryAttributeSetNode<T, AttributeSetRecordInputParameters<TAttribute, TAttributeSet>> {
    protected abstract get outputIndexingDim(): T

    protected override hasValidParameters(
        parameters: AttributeSetRecordInputParameters<TAttribute, TAttributeSet>,
    ): parameters is AttributeSetRecordInputParameters<TAttribute, TAttributeSet> &
        NonNullableParameters<AttributeSetRecordInputParameters<TAttribute, TAttributeSet>> {
        return Object.values(parameters.record).filter(IsNonNull).length > 0
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<AttributeSetRecordInputParameters<TAttribute, TAttributeSet>>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeSetData<T> | null>; invalidator?: BuilderInlet<null>} {
        const dim = this.outputIndexingDim

        const evaluatedRecord = scope.struct<{[K: string]: GeometryAttributeData<T> | GeometryAttributeSetData<T> | null}>(
            "evaluatedInputRecordEntries",
            mapFields(filterNullEntriesFromRecord(parameters.record), (node) => evaluator.evaluate(node)),
        )

        const geometryAttributeSetData = scope.pureLambda(
            scope.tuple(evaluatedRecord, dim),
            ([evaluatedInputRecordEntries, dim]) => setupAttributeSetDataFromRecordEntries(evaluatedInputRecordEntries, dim),
            "setupAttributeSetDataFromRecordEntries",
        )
        return {evaluatedGeometryData: geometryAttributeSetData}
    }
}
